import type { FC, ReactNode } from 'react'
import { useEffect } from 'react'
import { X, Search } from 'lucide-react'

export interface ActionButton {
  label: string
  onClick: () => void
  variant?: 'primary' | 'secondary' | 'danger'
}

interface Props {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  wide?: boolean
  actions?: ActionButton[]
  searchQuery?: string
  onSearchChange?: (query: string) => void
  searchPlaceholder?: string
}

const variantStyles: Record<string, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700',
  secondary: 'bg-slate-100 text-slate-600 hover:bg-slate-200',
  danger: 'bg-red-50 text-red-600 hover:bg-red-100',
}

const DashboardModal: FC<Props> = ({ open, onClose, title, children, wide, actions, searchQuery, onSearchChange, searchPlaceholder }) => {
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handler)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className='fixed inset-0 z-[1050] flex items-center justify-center p-4'>
      <div className='fixed inset-0 bg-black/40' onClick={onClose} />
      <div
        className={`relative bg-white rounded-2xl shadow-xl border border-slate-200 mx-4 w-full max-h-[85vh] flex flex-col ${wide ? 'max-w-4xl' : 'max-w-lg'}`}
        style={{ animation: 'modalFadeIn 0.2s ease-out' }}
      >
        {/* Header */}
        <div className='flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0'>
          <h3 className='text-base font-bold text-slate-900'>{title}</h3>
          <button type='button' onClick={onClose} className='p-1 rounded-lg hover:bg-slate-100 transition-colors'>
            <X className='w-4 h-4 text-slate-400' />
          </button>
        </div>

        {/* Search */}
        {searchQuery !== undefined && onSearchChange && (
          <div className='relative px-6 pt-3 pb-2 shrink-0'>
            <Search className='absolute left-8 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none' />
            <input
              type='text'
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder || 'Search...'}
              className='w-full pl-8 pr-3 py-1.5 text-[11px] border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400'
            />
          </div>
        )}

        {/* Body */}
        <div className='flex-1 overflow-y-auto px-6 py-4'>
          {children}
        </div>

        {/* Actions */}
        {actions && actions.length > 0 && (
          <div className='flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-100 shrink-0'>
            {actions.map((action, i) => (
              <button key={i} type='button' onClick={action.onClick}
                className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${variantStyles[action.variant || 'secondary']}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default DashboardModal
